import { SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode, forwardRef, ComponentProps } from 'react';
import Text from 'components/base/Text';
import { border4, pink_primary } from 'styles';

interface Props {
  /**
   * 좌측 커스텀 Dom 을 정의 합니다.
   */
  left?: ReactNode;
  /**
   * 우측 커스텀 Dom 을 정의 합니다.
   */
  right?: ReactNode;
  /**
   * Item 에 Title 을 정의 합니다.
   */
  title: string;
  /**
   * Title Text 스타일을 정의 합니다.
   * @default { variant: 'body2', color: 'gray_primary' }
   */
  title_style?: ComponentProps<typeof Text>;
  /**
   * Title 우측에 커스텀 Dom 을 정의합니다.
   */
  title_right?: ReactNode;
  /**
   * 부제목 을 정의 합니다.
   */
  sub_title?: string;
  /**
   * 부제목  Text 스타일을 정의 합니다.
   * @default { variant: 'body2', color: 'gray_tertiary' }

   */
  sub_title_style?: ComponentProps<typeof Text>;
  /**
   * description 을 정의 합니다.
   */
  description?: string;
  /**
   * Title 커스텀 Text 스타일을 정의 합니다.
   * @default { variant: 'small1_regular', color: 'gray_tertiary' }
   */
  description_style?: ComponentProps<typeof Text>;
  /**
   * 부제목에 dot 처리 스타일을 원할 경우 사용 합니다.
   */
  is_show_dot?: boolean;
  /**
   * Dom 에 classname 지정이 필요한 경우 사용합니다.
   */
  classname?: string;
  /**
   * 커스텀 스타일 수정을 위한 css를 정의 합니다.
   */
  css?: SerializedStyles;
  /**
   * Item 컴포넌트 클릭 이벤트
   */
  onClick?: () => void;
}

/**
 * 공통 ListItem 컴포넌트
 * @component
 */
const Item = forwardRef<HTMLLIElement, Props>(
  (
    {
      left,
      right,
      title,
      title_style = { variant: 'body2', color: 'gray_primary' },
      title_right,
      sub_title,
      sub_title_style = { variant: 'body2', color: 'gray_tertiary' },
      description,
      description_style = { variant: 'small1_regular', color: 'gray_tertiary' },
      is_show_dot,
      css,
      classname,
      ...props
    },
    ref,
  ) => {
    return (
      <SC.Container ref={ref} css={css} className={classname} {...props}>
        <div>
          <div className='title'>
            {left && <div className='left_info'>{left}</div>}
            <Text {...title_style} mr={4} as='p'>
              {title}
            </Text>
            {sub_title && (
              <Text {...sub_title_style} mr={4} as='p' mt={2}>
                {sub_title}
              </Text>
            )}
            {title_right && title_right}
            {is_show_dot && <SC.Dot />}
          </div>
          {description && (
            <Text {...description_style} mt={4} as='p'>
              {description}
            </Text>
          )}
        </div>
        {right && <div>{right}</div>}
      </SC.Container>
    );
  },
);

export default Item;

const SC = {
  Container: styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid ${border4};

    .title {
      display: flex;
      align-items: center;
    }

    .left_info {
      margin-right: 8px;
      display: flex;
    }

    &:last-child {
      border: none;
    }
  `,
  Dot: styled.div`
    width: 5px;
    height: 5px;
    border-radius: 5px;
    background-color: ${pink_primary};
  `,
};
